import { jsx as _jsx } from "react/jsx-runtime";
import { useRef, useState, useEffect } from "react";
import * as React from "react";
import { cls } from "@iscr/views/NUI/utils";
export const ImageZoom = ({ src, alt = "", max = 2.5, min = 1 }) => {
    const contRef = useRef(null);
    const imageRef = useRef(null);
    const [scale, setScale] = useState(1);
    const [translate, setTranslate] = useState({ x: 0, y: 0 });
    const lastDistance = useRef(null);
    const lastCenter = useRef(null);
    const isDragging = useRef(false);
    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
    const getDistance = (touch) => {
        if (touch.length < 2)
            return 0;
        const dx = touch[0].clientX - touch[1].clientX;
        const dy = touch[0].clientY - touch[1].clientY;
        return Math.hypot(dx, dy);
    };
    const getCenter = (touch) => {
        const x = (touch[0].clientX - touch[1].clientX) / 2;
        const y = (touch[0].clientY - touch[1].clientY) / 2;
        return { x, y };
    };
    const resetImage = () => {
        setScale(1);
        setTranslate({ x: 0, y: 0 });
    };
    // touch pad
    const handleWheel = (e) => {
        if (e.ctrlKey || Math.abs(e.deltaZ) < 0) {
            // zoom
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.1 : 0.1;
            let newScale = clamp(scale + delta, min, max);
            (newScale < scale && scale <= 1.5) || (newScale <= 1 && scale > 1) ? resetImage() : setScale(newScale);
        }
        else if (scale > 1) {
            // drag
            e.preventDefault();
            setTranslate(prev => ({
                x: prev.x - e.deltaX,
                y: prev.y - e.deltaY,
            }));
        }
    };
    // mobile pinch zoom
    const handleTouchStart = (e) => {
        if (e.touches.length === 2) {
            lastDistance.current = getDistance(e.touches);
            lastCenter.current = getCenter(e.touches);
        }
    };
    const handleTouchMove = (e) => {
        if (e.touches.length === 2) {
            e.preventDefault();
            const newDistance = getDistance(e.touches);
            const center = getCenter(e.touches);
            if (lastDistance.current && lastCenter.current) {
                const scaleChange = newDistance / lastDistance.current;
                const newScale = clamp(scale * scaleChange, min, max);
                newScale <= 1 && scale > 1 ? resetImage() : setScale(newScale);
                const dx = center.x - lastCenter.current.x;
                const dy = center.y - lastCenter.current.y;
                setTranslate(prev => ({
                    x: prev.x + dx,
                    y: prev.y + dy,
                }));
            }
            lastDistance.current = newDistance;
            lastCenter.current = center;
        }
    };
    const handleTouchEnd = () => {
        lastDistance.current = null;
        lastCenter.current = null;
    };
    const listeners = [
        { type: "wheel", handler: handleWheel, options: { passive: false } },
        { type: "touchstart", handler: handleTouchStart, options: { passive: false } },
        { type: "touchmove", handler: handleTouchMove, options: { passive: false } },
        { type: "touchend", handler: handleTouchEnd },
    ];
    useEffect(() => {
        const cont = contRef.current;
        if (!cont)
            return;
        listeners.forEach(({ type, handler, options }) => cont.addEventListener(type, handler, options));
        return () => {
            listeners.forEach(({ type, handler, options }) => cont.removeEventListener(type, handler, options));
        };
    }, [scale]);
    return (_jsx("div", { ref: contRef, className: cls(isDragging.current && "cursor-grabbing", "w-full h-full overflow-hidden flex items-center shrink-0 touch-auto snap-start"), children: _jsx("img", { src: src, alt: alt, ref: imageRef, draggable: false, className: "select-none w-full h-auto pointer-events-none", loading: "lazy", "data-testid": "image-gallery-main", style: {
                transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
                transformOrigin: "center",
                transition: isDragging.current ? "none" : "transform 0.2s ease-out",
            } }) }));
};
